import React, { useState, forwardRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Box,
    Divider,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    FormLabel,
    Slide,
    IconButton
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { theme } from '../../../../../theme';
import CloseIcon from '@mui/icons-material/Close';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';

const Transition = forwardRef(function Transition(
    props: TransitionProps & { children: React.ReactElement<any, any> },
    ref: React.Ref<unknown>
) {
    return <Slide direction="left" ref={ref} {...props} />;
});

enum DeclineReason {
    BetterOffer = 'I received a better offer elsewhere.',
    Raise = 'My current employer offered me a raise.',
    Personal = 'Personal reasons',
    Other = 'Other reasons'
}

interface DeclineOfferProps {
    open: boolean;
    onClose: (reason: string) => void;
    setDeclinePopupOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const DeclineOffer: React.FC<DeclineOfferProps> = ({
    open,
    onClose,
    setDeclinePopupOpen,
}) => {
    const [otherReasonText, setOtherReasonText] = useState('');
    const [selectedReason, setSelectedReason] = useState<string>('');
    const [reasonError, setReasonError] = useState(false);
    const [otherError, setOtherError] = useState(false);

    const handleReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedReason(event.target.value);
        setReasonError(false); // Hide error when a reason is selected
        setOtherError(false);  // Hide error when a reason is selected
        if (event.target.value !== DeclineReason.Other) {
            setOtherReasonText('');
        }
    };

    const handleSubmit = () => {
    if (!selectedReason) {
        setReasonError(true);
        return;
    }
    if (
        selectedReason === DeclineReason.Other &&
        (!otherReasonText.trim() || otherReasonText.length > 500)
    ) {
        setOtherError(true);
        return;
    }
    const decline_reason =
        selectedReason === DeclineReason.Other ? otherReasonText.trim() : selectedReason;
    onClose(decline_reason);
};



    const handleClosePopup = () => {
        setDeclinePopupOpen(false);
        setReasonError(false);
        setOtherError(false);
        setOtherReasonText('');
        setSelectedReason('');
    };

    const handleOtherReasonTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (e.target.value.length <= 500) {
        setOtherReasonText(e.target.value);
        setOtherError(false); // Hide error when user types
    }
};

    return (
        <Dialog
            open={open}
            onClose={handleClosePopup}
            TransitionComponent={Transition}
            fullScreen
            BackdropProps={{ style: { backgroundColor: 'transparent' } }}
            PaperProps={{
                sx: {
                    maxWidth: '22vw',
                    height: '100vh',
                    margin: 0,
                    overflow: 'hidden',
                    position: 'absolute',
                    right: 0
                }
            }}
        >
            {/* Header */}
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', p: '1.389vw 1.042vw 0 0.694vw' }}>
                <Box
                    color={theme.palette.secondary.main}
                    sx={{
                        border: '0.069vw solid #EEEEEE',
                        bgcolor: theme.palette.secondary.light,
                        margin: '0.417vw',
                        width: '2.361vw',
                        height: '2.361vw',
                        borderRadius: '0.694vw',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <SentimentVeryDissatisfiedIcon fontSize="large" />
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6">Decline Offer</Typography>
                </Box>
                <IconButton
                    edge="end"
                    color="inherit"
                    sx={{ position: 'relative', width: '2vw', height: '4vh' }}
                    onClick={handleClosePopup}
                    aria-label="close"
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Divider sx={{ mb: 1.2, mt: 2 }} />

            {/* Content */}
            <DialogContent sx={{ overflowY: 'auto', pt: 0 ,pr:0}}>
                <Typography gutterBottom variant="body2" sx={{ fontWeight: 'bold' }}>
                    We’re sorry to see you leave.
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    We value your feedback. Please let us know why you’re declining the offer.
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <FormControl component="fieldset">
                    <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: '0.556vw' }}>
                        Reason for declining the offer
                    </FormLabel>
                    <RadioGroup
                        value={selectedReason}
                        onChange={handleReasonChange}
                        name="decline-reason"
                    >
                        <FormControlLabel
                            value={DeclineReason.BetterOffer}
                            control={<Radio color="primary" />}
                            label={DeclineReason.BetterOffer}
                        />
                        <FormControlLabel
                            value={DeclineReason.Raise}
                            control={<Radio color="primary" />}
                            label={DeclineReason.Raise}
                        />
                        <FormControlLabel
                            value={DeclineReason.Personal}
                            control={<Radio color="primary" />}
                            label={DeclineReason.Personal}
                        />
                        <FormControlLabel
                            value={DeclineReason.Other}
                            control={<Radio color="primary" />}
                            label={DeclineReason.Other}
                        />
                    </RadioGroup>
                    {reasonError && (
                        <Typography sx={{ color: 'red', mt: 1 }}>
                            Please select a reason for declining the offer.
                        </Typography>
                    )}
                </FormControl>

                {/* Other reasons Section */}
                {selectedReason === DeclineReason.Other && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            Please specify your reason:
                        </Typography>
                        <textarea
                            placeholder="Enter your reason here"
                            value={otherReasonText}
                            onChange={handleOtherReasonTextChange}
                            required
                            style={{
                                width: '19vw',
                                height: '6.5vw',
                                marginTop: '0.5vw',
                                borderRadius: '0.7vw',
                                border: otherError ? '0.1vw solid red' : '0.1vw solid #ccc',
                                padding: '1vw',
                                fontSize: '1vw',
                                resize: 'none',
                                boxSizing: 'border-box'
                            }}
                        />
                        {otherError && (
                            <span style={{ color: 'red', fontSize: '0.9vw' }}>
                                Please provide a reason for declining the offer.
                            </span>
                        )}
                    </Box>
                )}
            </DialogContent>

            <Divider />

            {/* Footer */}
            <DialogActions sx={{ padding: 2 }}>
                <Button onClick={handleSubmit} variant="contained" color="primary" fullWidth>
                    Submit
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DeclineOffer;